import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { FaChevronLeft } from 'react-icons/fa'
import { Link, useParams, useHistory, useLocation } from 'react-router-dom'
import moment from 'moment'
import Modal from 'react-modal'
import * as firebase from 'firebase/app'
import 'firebase/firestore'

const modalStyles = {
	content: {
		top: '50%',
		left: '50%',
		right: 'auto',
		bottom: 'auto',
		marginRight: '-50%',
		transform: 'translate(-50%, -50%)',
		maxWidth: '448px',
		width: '100%',
		borderWidth: 0,
		padding: '24px',
		borderRadius: '16px',
		boxShadow: '0px 4px 8px rgba(0,0,0,0.16)',
	},
	overlay: {
		zIndex: 10,
		backgroundColor: 'rgba(0,0,0,0.2)',
	},
}

const TransactionEdit = () => {
	const [transaction, setTransaction] = useState<any>(null)
	const [user, setUser] = useState<any>(null)
	const [amount, setAmount] = useState<number | null>(null)
	const [slipNumber, setSlipNumber] = useState('')
	const [rewardAmount, setRewardAmount] = useState<number | null>(null)
	const [showRejectModal, setShowRejectModal] = useState(false)
	const [rejectionReason, setRejectionReason] = useState('unreadable-image')
	const [isPayout, setIsPayout] = useState(false)
	const [, setIsExisting] = useState(false)
	const [originalAmount, setOriginalAmount] = useState<number | null>(null)
	const [error, setError] = useState('')
	const [rejectionReasonDescription, setRejectionReasonDescription] = useState('')
	const [status, setStatus] = useState(0)

	const { state } = useLocation()

	let history = useHistory()
	const { transactionId } = useParams<any>()

	useEffect(
		() => {
			firebase.firestore().doc(`transactions/${transactionId}`).onSnapshot(snapshot => {
				const existing = snapshot.data()?.status > 1

				setTransaction(snapshot.data())
				setIsPayout(snapshot.data()?.amount < 0)
				setIsExisting(existing)
				setAmount(snapshot.data()?.amount)
				setRewardAmount(snapshot.data()?.rewardAmount)
				setSlipNumber(snapshot.data()?.slipNumber)
				setRejectionReason(snapshot.data()?.rejectionReason)
				setRejectionReasonDescription(snapshot.data()?.rejectionReasonDescription)
				setStatus(snapshot.data()?.status)

				if (existing) {
					setOriginalAmount(snapshot.data()?.amount)
				}
			})
		},
		[transactionId],
	)

	useEffect(
		() => {
			if (transaction) {
				firebase.firestore().doc(`users/${transaction.userId}`).onSnapshot(snapshot => {
					setUser(snapshot.data())
				})
			}
		},
		[transaction]
	)

	const isValid = useMemo(
		() => {
			if (isPayout) {
				return (!!rewardAmount && !!amount)
			}

			return !!amount && !!slipNumber
		},
		[amount, isPayout, rewardAmount, slipNumber]
	)

	const newBalance = useMemo(
		() => {
			if (originalAmount !== null) {
				return (amount || 0) - originalAmount + (user?.pointsBalance || 0)
			}

			return (user?.pointsBalance || 0) + (amount || 0)
		},
		[amount, originalAmount, user]
	)

	const handleApproveClick = useCallback(
		async () => {
			let t: any = {
				amount,
				status: 2,
				approvalDate: new Date(),
			}

			if (!isPayout) {
				const existing = await firebase.firestore().collection('transactions')
					.where('slipNumber', '==', slipNumber)
					.get()

				if (!existing.empty && existing.docs.findIndex(x => x.id !== transactionId) > -1) {
					setError('Could not save transaction: Slip number exists already')
					return
				}

				t.slipNumber = slipNumber
			} else {
				t.rewardAmount = rewardAmount
			}

			setError('')

			await firebase.firestore().doc(`transactions/${transactionId}`).update(t)
			history.push('/transactions', state)
		},
		[amount, history, isPayout, rewardAmount, slipNumber, state, transactionId],
	)

	const handleRejectClick = useCallback(
		async () => {
			await firebase.firestore().doc(`transactions/${transactionId}`).update({
				rejectionReason,
				rejectionReasonDescription,
				rejectionDate: new Date(),
				status: 3,
			})

			setShowRejectModal(false)

			// history.push('/transactions')
		},
		[rejectionReason, rejectionReasonDescription, transactionId],
	)

	const handleShowRejectClick = useCallback(
		() => {
			setShowRejectModal(true)
		},
		[],
	)

	const handleAmountChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const a = parseInt(e.target.value)
			setAmount(isNaN(a) ? null : a)
		},
		[],
	)

	const handleSlipNumberChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSlipNumber(e.target.value)
		},
		[]
	)

	const handleRewardAmountChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const a = parseInt(e.target.value)
			setRewardAmount(isNaN(a) ? null : a)
		},
		[],
	)

	const handleRejectionReasonChange = useCallback(
		(e) => {
			setRejectionReason(e.target.value)

			if (e.target.value !== 'other') {
				setRejectionReasonDescription('')
			}
		},
		[],
	)

	const rejectionReasons = useMemo(
		() => {
			if (isPayout) {
				return [
					{ value: 'already-paid-out', text: 'Claim already paid out.' },
					{ value: 'claim-exceeds-points-balance', text: 'Claim exceeds available points balance.' },
					{ value: 'claim-exceeds-daily-limit', text: 'Claim exceeds daily claim limit' },
					{ value: 'other', text: 'Other' },
				]
			}

			return [
				{ value: 'unreadable-image', text: 'Unreadable image' },
				{ value: 'incorrect-product-vendor', text: 'Product(s) not from KNR' },
				{ value: 'already-claimed', text: 'Already claimed' },
				{ value: 'product-ineligible', text: 'Product(s) not eligible' },
				{ value: 'incorrect-consultant', text: 'Slip has a different consultant name' },
				{ value: 'other', text: 'Other' },
			]
		},
		[isPayout],
	)

	if (!transaction) {
		return null
	}

	return (
		<div className="container mx-auto">
			<div>
				<Link className="flex items-center group" to={{ pathname: '../transactions', state }}>
					<FaChevronLeft className="text-black group-hover:text-red-600" />
					<div className="ml-2 text-sm font-semibold group-hover:text-red-600">Transactions</div>
				</Link>
				<div className="w-full max-w-screen-md mx-auto mt-4">
					{
						error && (
							<div className="bg-red-200 text-red-700 font-bold text-sm rounded p-2 px-4 mb-4">
								{error}
							</div>
						)
					}
					{
						isPayout ? (
							<h2 className="text-gray-900">Payout Request</h2>
						) : (
								<h2 className="text-gray-900">Sale Capture Request</h2>
							)
					}
					<div className="bg-white rounded shadow-xs mt-4">
						<div className="p-4 flex">
							{
								!isPayout && (
									<div className="mr-4">
										<a href={transaction.receiptImage} target="_blank" rel="noopener noreferrer">
											<img src={transaction.receiptImage} className="w-64 rounded" alt="Receipt" />
										</a>
									</div>
								)
							}
							<table className="text-left text-sm">
								<tbody>
									<tr>
										<th className="pr-2 leading-relaxed">Consultant Name</th>
										<td>{transaction.userName}</td>
									</tr>
									<tr>
										<th className="pr-2 leading-relaxed">Consultant Code</th>
										<td>{transaction.consultantCode}</td>
									</tr>
									<tr>
										<th className="pr-2 leading-relaxed">Consultant Phone Number</th>
										<td>{user?.phoneNumber}</td>
									</tr>
									<tr>
										<th className="pr-2 leading-relaxed">Transaction Date</th>
										<td>
											{moment(transaction.transactionDate.toDate()).format('YYYY-MM-DD hh:mm:ss A')}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
						{
							!isPayout && (
								<div className="border-t p-4">
									<h3 className="text-sm font-semibold mb-2">Slip Number</h3>
									<div className="flex">
										<input value={slipNumber} onChange={handleSlipNumberChange} type="text" placeholder="" />
									</div>
								</div>
							)
						}
						<div className="border-t p-4">
							<h3 className="text-sm font-semibold mb-2">Assign points</h3>
							<div className="flex">
								<input value={amount?.toString()} onChange={handleAmountChange} type="text" placeholder="0" />
							</div>
						</div>
						{
							isPayout && (
								<div className="border-t p-4">
									<h3 className="text-sm font-semibold mb-2">Rand Value</h3>
									<div className="flex">
										<input value={rewardAmount?.toString()} onChange={handleRewardAmountChange} type="text" placeholder="0.00" />
									</div>
								</div>
							)
						}
						<div className="border-t p-4">
							<h3 className="text-sm font-semibold mb-2">New Balance</h3>
							<div>
								{newBalance}
							</div>
						</div>
						{
							status === 3
							&& (
								<div className="border-t p-4">
									<h3 className="text-sm font-semibold mb-2">Rejection reason</h3>
									<div>
										{
											rejectionReasonDescription?.trim() !== ''
												? rejectionReasonDescription
												: rejectionReasons.find(x => x.value === rejectionReason)?.text
										}
									</div>
								</div>
							)
						}
					</div>
					<div className="flex justify-end">
						<button onClick={handleShowRejectClick} className="mt-4 bg-red-600">Reject</button>
						<button disabled={!isValid} onClick={handleApproveClick} className="mt-4 ml-2 bg-indigo-600">Approve</button>
					</div>
				</div>
			</div>
			<Modal onRequestClose={() => { setShowRejectModal(false) }} style={modalStyles} isOpen={showRejectModal}>
				<h2 className="mb-4">Reject transaction</h2>
				<p className="mb-6 text-sm text-darkGrey">
					Please select a rejection reason below
				</p>
				<div className="mb-4 flex flex-col">
					<label>Rejection reason:</label>
					<select value={rejectionReason} onChange={handleRejectionReasonChange}>
						{
							rejectionReasons.map(r => (
								<option key={r.value} value={r.value}>{r.text}</option>
							))
						}
					</select>
					{
						rejectionReason === 'other'
						&& (
							<>
								<label>Please specify rejection reason:</label>
								<input type="text" value={rejectionReasonDescription} onChange={e => { setRejectionReasonDescription(e.target.value) }} />
							</>
						)
					}
				</div>
				<div className="flex">
					<button onClick={() => { setShowRejectModal(false) }} className="flex-1 secondary model">
						Cancel
					</button>
					<button onClick={handleRejectClick} className="flex-1 ml-4 bg-red model">
						Reject
					</button>
				</div>
			</Modal>
		</div>
	)
}

export default TransactionEdit
